import React, { useState, useEffect, useMemo } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";

// Lodash
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

// Config
import { userTypes } from "../../config/values";
import colors from "../../config/colors";
import styles from "../../config/styles";
import useIncidentTypes from "../../hooks/useIncidentTypes";

// Components
import FormikHandler from "../../components/FormikHandler";
import LoadingSection from "../../components/LoadingSection/LoadingSection";
import SendEvidenceDropdown from "../SendEvidencePage/components/SendEvidenceDropdown";
import ShareWithFormHandler from "./ShareWithFormHandler";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border,
    marginBottom: theme.spacing(1.5),
    overflow: "hidden"
  },
  rootIntel: {
    borderColor: colors.royalBlue
  }
}));

const ShareWithOptions = () => {
  const classes = useStyles();

  // State
  const [coord, setCoord] = useState({});
  const [initialized, setInitialized] = useState(false);

  // Data and Params
  const { data, isLoading } = useSelector(s => s.caseDetail);
  const { userCapacity, incident = {} } = data || {};
  const incidentTypes = useIncidentTypes();

  // Flags
  const isVictim = userCapacity === userTypes.Victim;
  const isPolice = userCapacity === "PoliceOfficer";
  const canRequest = data.isOwnedByUser || isVictim || isPolice;

  useEffect(() => {
    if (!initialized && !isEmpty(incident)) {
      setInitialized(true);
      setCoord(get(incident, "location.coordinate"));
    }
  }, [incident, initialized]);

  const location = get(incident, "location.name", "");
  const incidentTypeId = get(incident, "incidentTypeId", "");

  const incidentType = useMemo(() => {
    if (incidentTypes.isLoading || !incidentTypes.data) return null;
    const item = incidentTypes.data.find(({ id }) => id === incidentTypeId);
    // IncidentTypeField's Autocomplete options (and a user's own selection)
    // are shaped {value, text} - the initial value has to match that shape,
    // not the raw incident-type item, or Autocomplete can't match it to an
    // option.
    return item ? { value: item.id, text: item.name } : null;
  }, [incidentTypeId, incidentTypes]);

  // Formik's initialValues is only read once, at mount - if incidentTypes
  // hasn't loaded yet at that moment, incidentType below evaluates to null,
  // and the form is permanently stuck without one even after incidentTypes
  // finishes loading a moment later (Formik doesn't reactively pick up a
  // later initialValues change). Waiting for both isLoading and incidentTypes
  // together (matching CaseIncidentsSubmit.jsx's own gate) avoids the race -
  // most reproducible on a hard refresh, where both fetches start from zero
  // instead of incidentTypes already being cached from earlier navigation.
  if (isLoading || incidentTypes.isLoading || !incidentTypes.data) {
    return (
      <Paper elevation={0} className={classes.root}>
        <LoadingSection normalHeight noBackground />
      </Paper>
    );
  }

  return (
    <>
      <FormikHandler
        formik={{
          initialValues: { location, incidentTypeId: incidentType },
          validateOnBlur: false,
          validateOnChange: false
        }}
        render={formikProps => (
          <ShareWithFormHandler
            {...formikProps}
            coord={coord}
            setCoord={setCoord}
          />
        )}
      />
      <Paper
        elevation={0}
        className={clsx(classes.root, {
          [classes.rootIntel]: data.isIntel
        })}
      >
        <SendEvidenceDropdown canRequest={canRequest} isIntel={data.isIntel} />
      </Paper>
    </>
  );
};

export default ShareWithOptions;
